import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Button, Form, Alert } from 'react-bootstrap';
import { SearchableCustomerSelect } from './SearchableCustomerSelect';
import { SearchableVehicleSelect } from './SearchableVehicleSelect';
import { renderPaymentMethodOptions } from '../utils/paymentMethodOptions';
import { API_BASE_URL } from '../config/api';
import { pricingTiers } from '../utils/transactionPayload';
import { calculateTaxFrontend } from '../config/taxRates';

/**
 * TransactionForm - Shared form component for creating/editing transactions
 * @param {Object} props
 * @param {Object} props.initialData - Initial form data (for edit mode)
 * @param {Function} props.onSubmit - Submit handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {Function} props.apiCall - Authenticated API call function
 * @param {string} props.submitLabel - Label for submit button (default: "Save Transaction")
 * @param {Object} props.user - Current user object
 */
export function TransactionForm({
  initialData = {},
  onSubmit,
  onCancel,
  apiCall,
  submitLabel = "Save Transaction",
  user = {}
}) {
  const [formData, setFormData] = useState({
    customer_id: '',
    type: 'rental',
    store_id: '',
    vehicle_id: '',
    insurance_id: '',
    start_date: new Date().toISOString().split('T')[0],
    unit: 'day',
    quantity: '',
    amount: '',
    contract_file_url: '',
    notes: '',
    isPaid: true,
    payment_method: 'cash_app',
    ...initialData
  });
  const [formError, setFormError] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [amountOverridden, setAmountOverridden] = useState(false);

  // Fetch vehicle details when vehicle_id changes (for pricing calculation)
  const handleVehicleSelect = useCallback(async (vehicleId) => {
    if (vehicleId && apiCall) {
      try {
        const res = await apiCall(`${API_BASE_URL}/vehicles/${vehicleId}`);
        setSelectedVehicle(res?.data || null);
        setFormData(prev => ({ ...prev, vehicle_id: vehicleId, vehicle_class: res?.data?.class || null }));
      } catch {
        setSelectedVehicle(null);
        setFormData(prev => ({ ...prev, vehicle_id: vehicleId, vehicle_class: null }));
      }
    } else {
      setSelectedVehicle(null);
      setFormData(prev => ({ ...prev, vehicle_id: '', vehicle_class: null }));
    }
  }, [apiCall]);

  // Update form data if initialData changes (for edit mode)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  // Set store_id if user is a manager with a store
  useEffect(() => {
    if (user?.role === 'manager' && user?.store_id && !formData.store_id) {
      setFormData(prev => ({ ...prev, store_id: user.store_id.toString() }));
    }
  }, [user]);

  // Auto-calculate amount when vehicle, unit, or quantity changes (unless user overrode it)
  useEffect(() => {
    if (amountOverridden) return;
    if (formData.type === 'rental' && selectedVehicle && formData.quantity) {
      const pricing = pricingTiers[selectedVehicle.class];
      if (pricing) {
        const quantity = parseInt(formData.quantity);
        let calculated = 0;
        if (formData.unit === 'day') calculated = pricing.daily * quantity;
        else if (formData.unit === 'week') calculated = pricing.weekly * quantity;
        else if (formData.unit === 'month') calculated = pricing.monthly * quantity;
        setFormData(prev => ({ ...prev, amount: calculated.toFixed(2) }));
      }
    }
  }, [selectedVehicle, formData.unit, formData.quantity, formData.type, amountOverridden]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'amount') setAmountOverridden(true);
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    if (formData.type === 'rental' && formData.quantity) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(startDate);
      const quantity = parseInt(formData.quantity);
      if (formData.unit === 'day') endDate.setDate(endDate.getDate() + quantity);
      else if (formData.unit === 'week') endDate.setDate(endDate.getDate() + quantity * 7);
      else if (formData.unit === 'month') endDate.setMonth(endDate.getMonth() + quantity);

      if (endDate <= startDate) {
        setFormError('End date must be after start date');
        return;
      }
      const rentalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      if (rentalDays > 183) {
        setFormError('Rental duration cannot exceed 6 months (183 days)');
        return;
      }
    }

    if (['rental', 'fee'].includes(formData.type) && parseFloat(formData.amount) <= 0) {
      setFormError('Amount must be greater than 0');
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      setFormError(error.message || 'An error occurred');
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {formError && <Alert variant="danger">{formError}</Alert>}

      <Row>
        <Col md={6}>
          <SearchableCustomerSelect
            apiCall={apiCall}
            selectedCustomerId={formData.customer_id}
            onSelectCustomer={(customerId) => setFormData({ ...formData, customer_id: customerId })}
            required={true}
          />
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Select
              name="type"
              value={formData.type}
              onChange={(e) => {
                handleInputChange(e);
                if (e.target.value === 'fee') {
                  setFormData(prev => ({
                    ...prev,
                    type: 'fee',
                    store_id: '',
                    vehicle_id: '',
                    unit: 'day',
                    quantity: '',
                  }));
                }
              }}
            >
              <option value="rental">Rental Income</option>
              <option value="fee">Other Charges</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={formData.type === 'rental' ? 4 : 12}>
          <Form.Group className="mb-3">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleInputChange}
              required
            />
          </Form.Group>
        </Col>
        {formData.type === 'rental' && (
          <>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Unit</Form.Label>
                <Form.Select
                  name="unit"
                  value={formData.unit}
                  onChange={handleInputChange}
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Quantity</Form.Label>
                <Form.Control
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  min="1"
                />
                {formData.start_date && formData.quantity && (
                  (() => {
                    const startDate = new Date(formData.start_date);
                    const endDate = new Date(startDate);
                    const quantity = parseInt(formData.quantity);
                    if (formData.unit === 'day') endDate.setDate(endDate.getDate() + quantity);
                    else if (formData.unit === 'week') endDate.setDate(endDate.getDate() + quantity * 7);
                    else if (formData.unit === 'month') endDate.setMonth(endDate.getMonth() + quantity);
                    const rentalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
                    const over6Months = rentalDays > 183;
                    return (
                      <>
                        <Form.Text className={over6Months ? 'text-danger' : 'text-muted'}>
                          End Date: {endDate.toISOString().split('T')[0]}
                          {over6Months && ' — exceeds 6-month maximum'}
                        </Form.Text>
                      </>
                    );
                  })()
                )}
              </Form.Group>
            </Col>
          </>
        )}
      </Row>

      <Row>
        <Col md={12}>
          <SearchableVehicleSelect
            apiCall={apiCall}
            selectedVehicleId={formData.vehicle_id}
            onSelectVehicle={handleVehicleSelect}
            storeId={formData.store_id || null}
            required={formData.type === 'rental'}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              step="0.01"
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Paid</Form.Label>
            <Form.Select
              name="isPaid"
              value={formData.isPaid}
              onChange={(e) => {
                const syntheticEvent = {
                  target: { name: 'isPaid', value: e.target.value === 'true' }
                };
                handleInputChange(syntheticEvent);
              }}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Tax Preview for Rentals */}
      {formData.type === 'rental' && selectedVehicle && parseFloat(formData.amount) > 0 && formData.quantity && (() => {
        const quantity = parseInt(formData.quantity);
        let rentalDays = quantity;
        if (formData.unit === 'week') rentalDays = quantity * 7;
        else if (formData.unit === 'month') rentalDays = quantity * 30;
        const taxResult = calculateTaxFrontend(selectedVehicle.store_id, parseFloat(formData.amount), rentalDays);
        if (taxResult.totalTax > 0) {
          const grandTotal = parseFloat(formData.amount) + taxResult.totalTax;
          return (
            <Row>
              <Col md={12}>
                <div className="mb-3 p-3 border rounded" style={{ backgroundColor: '#fff8f0', borderColor: '#e67e22' }}>
                  <strong style={{ color: '#e67e22' }}>Tax Estimate</strong>
                  <table className="mt-2" style={{ width: '100%' }}>
                    <tbody>
                      <tr>
                        <td>Subtotal (Rental)</td>
                        <td className="text-end">${parseFloat(formData.amount).toFixed(2)}</td>
                      </tr>
                      {taxResult.items.map((item, i) => (
                        <tr key={i} style={{ color: '#e67e22' }}>
                          <td>{item.name} ({item.type === 'percentage' ? `${(item.rate * 100).toFixed(2)}%` : `$${item.rate.toFixed(2)}/day`})</td>
                          <td className="text-end">${item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                      <tr style={{ borderTop: '1px solid #e67e22', fontWeight: 'bold' }}>
                        <td>Tax Total</td>
                        <td className="text-end">${taxResult.totalTax.toFixed(2)}</td>
                      </tr>
                      <tr style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                        <td>Grand Total (incl. tax)</td>
                        <td className="text-end">${grandTotal.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Col>
            </Row>
          );
        }
        return null;
      })()}

      {formData.isPaid && (
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Payment Method</Form.Label>
              <Form.Select
                name="payment_method"
                value={formData.payment_method}
                onChange={handleInputChange}
              >
                {renderPaymentMethodOptions()}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      )}

      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex gap-2">
        <Button type="submit" variant="primary">
          {submitLabel}
        </Button>
        {onCancel && (
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </Form>
  );
}
